import './index.css'

import React, {useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useSearchHotelsQuery} from 'services/partnersApi'
import {getPapiLabel, WidgetLabelTypes} from 'utils/label'

import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity
} from '@daedalus/core/src/analytics/types/Events'
import {availableCountries} from '@daedalus/core/src/localization/business/countries'
import {TaxDisplayLogic} from '@daedalus/core/src/offer/types/TaxDisplayLogic'
import {roomsSplitToString} from '@daedalus/core/src/room/business/roomConfiguration'
import {UniqueRoomSplitType} from '@daedalus/core/src/room/types/RoomConfiguration'

import {SearchQuery} from '../../types/partnersApi/types'
import {buildWidgetConfig} from '../helpers/widgetConfig'
import {HotelCard} from './HotelCard/HotelCard'

const MAX_OFFERS = 3

export const HotelCardWidget = ({
  partnerKey,
  hotelNames,
  hotelIds,
  country,
  city,
  checkIn,
  checkOut,
  rooms,
  language,
  currency,
  anonymousId,
  userDevice,
  userCountry,
  distanceUnit,
  forcedUserCountry,
  label
}: Awaited<ReturnType<typeof buildWidgetConfig>>) => {
  const dispatch = useDispatch()
  const [currentCheckIn, setCurrentCheckIn] = useState(checkIn)
  const [currentCheckOut, setCurrentCheckOut] = useState(checkOut)
  // This one is needed to not trigger request when we setting up dates after first search response
  const [isSkipRequest, setIsSkipRequest] = useState(false)

  const [currentRooms, setCurrentRooms] = useState(rooms)

  const query: SearchQuery = hotelIds.length
    ? {hotelIds}
    : {
        hotelNames: {
          names: hotelNames,
          country,
          city
        }
      }

  const {data, error, isFetching, refetch} = useSearchHotelsQuery(
    {
      partnerKey,
      payload: {
        query,
        hotelAttributes: ['details'],
        language,
        currency,
        anonymousId,
        userDevice,
        userCountry: forcedUserCountry,
        checkIn: currentCheckIn,
        checkOut: currentCheckOut,
        rooms: currentRooms,
        label: getPapiLabel(WidgetLabelTypes.hotelCard, label)
      },
      widgetType: WidgetLabelTypes.hotelCard
    },
    {
      refetchOnMountOrArgChange: true,
      skip: isSkipRequest
    }
  )
  const hotel = data?.hotels[0]

  const taxDisplayLogic: TaxDisplayLogic = availableCountries[userCountry]

  const onDatesUpdate = (checkIn: string, checkOut: string) => {
    setCurrentCheckIn(checkIn)
    setCurrentCheckOut(checkOut)
  }

  const onDatesSubmit = () => {
    setIsSkipRequest(false)
  }

  const onGuestsUpdate = (rooms: UniqueRoomSplitType[]) => {
    const newRooms = roomsSplitToString(rooms)
    setCurrentRooms(newRooms)
    setIsSkipRequest(false)
  }

  const stay = data?.stay

  useEffect(() => {
    if (stay?.checkIn && stay?.checkOut) {
      setCurrentCheckIn(stay.checkIn)
      setCurrentCheckOut(stay.checkOut)
      setIsSkipRequest(true)
    }
  }, [stay?.checkIn, stay?.checkOut])

  useEffect(() => {
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.Widget,
        action: Action.Displayed,
        payload: {
          partnerKey,
          widgetType: WidgetLabelTypes.hotelCard,
          label
        }
      })
    )
  }, [])

  return (
    <HotelCard
      checkIn={currentCheckIn}
      checkOut={currentCheckOut}
      rooms={currentRooms}
      offers={hotel?.offers.slice(0, MAX_OFFERS)}
      hotelName={hotel?.details?.name}
      starRating={hotel?.details?.starRating}
      address={hotel?.details?.address}
      hotelImages={hotel?.details?.images}
      distanceFromTarget={hotel?.details?.distanceFromCityCentre}
      cityName={hotel?.details?.cityName}
      distanceUnit={distanceUnit}
      onDatesUpdate={onDatesUpdate}
      onGuestsUpdate={onGuestsUpdate}
      onDatesSubmit={onDatesSubmit}
      hasError={!!error}
      isFetching={isFetching}
      refetch={refetch}
      taxDisplayLogic={taxDisplayLogic}
      partnerKey={partnerKey}
      label={label}
    />
  )
}
